import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, Scale, HardHat, X, Check, ArrowRight, Search } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";

const ICONS = [BadgeCheck, HardHat, Scale, ShieldCheck];

const C = {
  sk: {
    brand: "SauberEko® je nová značka, ktorá stojí na strane profesionality a kvality — nie na strane lacnej ceny.",
    aboutLink: "Viac o nás",
    kicker: "Pečať SauberEko® – istota pre investora",
    title: "Platíte za profesionálov, dostanete profesionálov",
    lead: "Ako investor alebo hlavný dodávateľ často neviete, koho vám subdodávateľ na stavbu skutočne pošle. Na faktúre je rovnaká hodinová sadzba – no na mieste pracuje raz skúsený profesionál, inokedy začiatočník. Firma s pečaťou SauberEko® vám dáva záruku pravdy o kompetenciách, profesionalite, odbornosti a reálnej praxi nasadeného personálu.",
    gTitle: "Čo vám pečať SauberEko® garantuje",
    g: [
      { h: "Overené kompetencie", t: "Každý certifikovaný pracovník prešiel odborným preverením. Jeho zručnosti sú zapísané v Sieni slávy a overíte ich podľa mena za pár sekúnd." },
      { h: "Reálna prax, nie len papier", t: "Posudzujeme skutočnú prax na projektoch pod dohľadom mentora – nie iba kurzy a osvedčenia v šuplíku." },
      { h: "Férová cena podľa úrovne", t: "Subdodávateľ už nemôže fakturovať rovnakú cenu za profesionála aj za začiatočníka. Úroveň každého nasadeného pracovníka je transparentná." },
      { h: "Firma nasadzuje profesionálov", t: "Firma s pečaťou sa zaväzuje nasadzovať na vaše zákazky profesionálov – a začiatočníkov iba pod ich priamym dohľadom." },
    ],
    cTitle: "Rozdiel, ktorý uvidíte na stavbe aj na faktúre",
    without: "Bez pečate",
    with: "S pečaťou SauberEko®",
    rows: [
      ["Neviete, kto na zákazke reálne pracuje", "Každý nasadený pracovník je overiteľný podľa mena"],
      ["Jednotná cena za profesionála aj začiatočníka", "Cena zodpovedá skutočnej úrovni personálu"],
      ["Kvalita sa ukáže až pri chybách a reklamáciách", "Odbornosť a prax sú preverené vopred"],
      ["Sľuby v ponuke bez dôkazu", "Nezávislá záruka pod ochrannou známkou EÚ SauberEko®"],
    ],
    note: "Rovnako ako v sekcii O nás: SauberEko® nie je úrad, ale značka profesionality a odbornosti. Pečať dostane len firma, ktorá svoju kvalitu preukáže v praxi.",
    ctaTitle: "Overte si firmu skôr, než podpíšete zmluvu",
    ctaLead: "V Sieni slávy nájdete certifikované firmy aj profesionálov. Stačí zadať názov firmy alebo priezvisko.",
    cta: "Overiť firmu v Sieni slávy",
    metaDesc: "Pečať SauberEko® – istota pre investora: záruka pravdy o kompetenciách, odbornosti a reálnej praxi nasadeného personálu. Férová cena podľa skutočnej úrovne pracovníkov.",
  },
  en: {
    brand: "SauberEko® is a new brand that stands on the side of professionalism and quality — not on the side of the cheapest price.",
    aboutLink: "More about us",
    kicker: "The SauberEko® seal – certainty for the investor",
    title: "You pay for professionals, you get professionals",
    lead: "As an investor or main contractor, you often don't know who a subcontractor will actually send to your site. The invoice shows the same hourly rate – yet on site it may be an experienced professional one day and a beginner the next. A company with the SauberEko® seal gives you a guarantee of truth about the competence, professionalism, expertise and real-world experience of the staff it deploys.",
    gTitle: "What the SauberEko® seal guarantees you",
    g: [
      { h: "Verified competence", t: "Every certified worker has passed an expert review. Their skills are listed in the Hall of Fame and you can verify them by name in seconds." },
      { h: "Real experience, not just paper", t: "We assess genuine experience on projects under a mentor's supervision – not just courses and certificates in a drawer." },
      { h: "A fair price for each level", t: "A subcontractor can no longer charge the same rate for a professional and a beginner. The level of every deployed worker is transparent." },
      { h: "The company deploys professionals", t: "A company with the seal commits to deploying professionals on your contracts – and beginners only under their direct supervision." },
    ],
    cTitle: "A difference you will see on site and on the invoice",
    without: "Without the seal",
    with: "With the SauberEko® seal",
    rows: [
      ["You don't know who is actually working on your contract", "Every deployed worker can be verified by name"],
      ["One price for a professional and a beginner alike", "The price reflects the real level of the staff"],
      ["Quality only shows when errors and claims appear", "Expertise and experience are verified in advance"],
      ["Promises in the offer without proof", "An independent guarantee under the EU trademark SauberEko®"],
    ],
    note: "Just as in our About us section: SauberEko® is not an authority but a brand of professionalism and expertise. Only a company that proves its quality in practice receives the seal.",
    ctaTitle: "Verify the company before you sign the contract",
    ctaLead: "In the Hall of Fame you will find certified companies and professionals. Just enter the company name or a surname.",
    cta: "Verify a company in the Hall of Fame",
    metaDesc: "The SauberEko® seal – certainty for the investor: a guarantee of truth about the competence, expertise and real experience of deployed staff. A fair price based on the real level of workers.",
  },
  de: {
    brand: "SauberEko® ist eine neue Marke, die auf der Seite von Professionalität und Qualität steht — nicht auf der Seite des billigsten Preises.",
    aboutLink: "Mehr über uns",
    kicker: "Das SauberEko®-Siegel – Sicherheit für den Investor",
    title: "Sie zahlen für Profis, Sie bekommen Profis",
    lead: "Als Investor oder Generalunternehmer wissen Sie oft nicht, wen ein Subunternehmer tatsächlich auf Ihre Baustelle schickt. Auf der Rechnung steht derselbe Stundensatz – vor Ort arbeitet jedoch mal ein erfahrener Profi, mal ein Anfänger. Ein Unternehmen mit dem SauberEko®-Siegel gibt Ihnen eine Garantie der Wahrheit über Kompetenz, Professionalität, Fachwissen und reale Praxis des eingesetzten Personals.",
    gTitle: "Was Ihnen das SauberEko®-Siegel garantiert",
    g: [
      { h: "Geprüfte Kompetenz", t: "Jeder zertifizierte Mitarbeiter hat eine fachliche Prüfung bestanden. Seine Fähigkeiten stehen in der Ruhmeshalle und lassen sich in Sekunden per Namen überprüfen." },
      { h: "Echte Praxis, nicht nur Papier", t: "Wir bewerten echte Praxis auf Projekten unter Aufsicht eines Mentors – nicht nur Kurse und Zertifikate in der Schublade." },
      { h: "Faire Preise je nach Niveau", t: "Ein Subunternehmer kann für einen Profi und einen Anfänger nicht mehr denselben Preis abrechnen. Das Niveau jedes eingesetzten Mitarbeiters ist transparent." },
      { h: "Das Unternehmen setzt Profis ein", t: "Ein Unternehmen mit Siegel verpflichtet sich, auf Ihren Aufträgen Profis einzusetzen – und Anfänger nur unter deren direkter Aufsicht." },
    ],
    cTitle: "Ein Unterschied, den Sie auf der Baustelle und auf der Rechnung sehen",
    without: "Ohne Siegel",
    with: "Mit dem SauberEko®-Siegel",
    rows: [
      ["Sie wissen nicht, wer an Ihrem Auftrag wirklich arbeitet", "Jeder eingesetzte Mitarbeiter ist per Namen überprüfbar"],
      ["Ein Einheitspreis für Profi und Anfänger", "Der Preis entspricht dem tatsächlichen Niveau des Personals"],
      ["Qualität zeigt sich erst bei Fehlern und Reklamationen", "Fachwissen und Praxis sind im Voraus geprüft"],
      ["Versprechen im Angebot ohne Nachweis", "Eine unabhängige Garantie unter der EU-Marke SauberEko®"],
    ],
    note: "Genau wie im Bereich Über uns: SauberEko® ist keine Behörde, sondern eine Marke für Professionalität und Fachkompetenz. Das Siegel erhält nur ein Unternehmen, das seine Qualität in der Praxis beweist.",
    ctaTitle: "Prüfen Sie das Unternehmen, bevor Sie den Vertrag unterschreiben",
    ctaLead: "In der Ruhmeshalle finden Sie zertifizierte Unternehmen und Profis. Geben Sie einfach den Firmennamen oder einen Nachnamen ein.",
    cta: "Unternehmen in der Ruhmeshalle prüfen",
    metaDesc: "Das SauberEko®-Siegel – Sicherheit für den Investor: Garantie der Wahrheit über Kompetenz, Fachwissen und reale Praxis des eingesetzten Personals. Faire Preise nach dem tatsächlichen Niveau.",
  },
  pl: {
    brand: "SauberEko® to nowa marka, która stoi po stronie profesjonalizmu i jakości — a nie najniższej ceny.",
    aboutLink: "Więcej o nas",
    kicker: "Pieczęć SauberEko® – pewność dla inwestora",
    title: "Płacisz za profesjonalistów, dostajesz profesjonalistów",
    lead: "Jako inwestor lub generalny wykonawca często nie wiesz, kogo podwykonawca faktycznie przyśle na Twoją budowę. Na fakturze widnieje ta sama stawka godzinowa – a na miejscu pracuje raz doświadczony profesjonalista, a raz początkujący. Firma z pieczęcią SauberEko® daje Ci gwarancję prawdy o kompetencjach, profesjonalizmie, fachowości i rzeczywistej praktyce zatrudnionego personelu.",
    gTitle: "Co gwarantuje Ci pieczęć SauberEko®",
    g: [
      { h: "Zweryfikowane kompetencje", t: "Każdy certyfikowany pracownik przeszedł fachową weryfikację. Jego umiejętności są wpisane do Galerii Sław i sprawdzisz je po nazwisku w kilka sekund." },
      { h: "Prawdziwa praktyka, nie tylko papier", t: "Oceniamy rzeczywistą praktykę na projektach pod nadzorem mentora – nie tylko kursy i zaświadczenia w szufladzie." },
      { h: "Uczciwa cena według poziomu", t: "Podwykonawca nie może już fakturować tej samej stawki za profesjonalistę i za początkującego. Poziom każdego pracownika jest przejrzysty." },
      { h: "Firma kieruje profesjonalistów", t: "Firma z pieczęcią zobowiązuje się kierować do Twoich zleceń profesjonalistów – a początkujących wyłącznie pod ich bezpośrednim nadzorem." },
    ],
    cTitle: "Różnica, którą zobaczysz na budowie i na fakturze",
    without: "Bez pieczęci",
    with: "Z pieczęcią SauberEko®",
    rows: [
      ["Nie wiesz, kto faktycznie pracuje przy Twoim zleceniu", "Każdego pracownika można zweryfikować po nazwisku"],
      ["Jedna cena za profesjonalistę i początkującego", "Cena odpowiada rzeczywistemu poziomowi personelu"],
      ["Jakość wychodzi na jaw dopiero przy błędach i reklamacjach", "Fachowość i praktyka są sprawdzone z góry"],
      ["Obietnice w ofercie bez dowodu", "Niezależna gwarancja pod znakiem towarowym UE SauberEko®"],
    ],
    note: "Tak jak w sekcji O nas: SauberEko® nie jest urzędem, lecz marką profesjonalizmu i fachowości. Pieczęć otrzymuje tylko firma, która udowodni swoją jakość w praktyce.",
    ctaTitle: "Sprawdź firmę, zanim podpiszesz umowę",
    ctaLead: "W Galerii Sław znajdziesz certyfikowane firmy i profesjonalistów. Wystarczy wpisać nazwę firmy lub nazwisko.",
    cta: "Sprawdź firmę w Galerii Sław",
    metaDesc: "Pieczęć SauberEko® – pewność dla inwestora: gwarancja prawdy o kompetencjach, fachowości i rzeczywistej praktyce personelu. Uczciwa cena według rzeczywistego poziomu pracowników.",
  },
  fr: {
    brand: "SauberEko® est une nouvelle marque qui se tient du côté du professionnalisme et de la qualité — et non du prix le plus bas.",
    aboutLink: "En savoir plus sur nous",
    kicker: "Le sceau SauberEko® – la certitude pour l'investisseur",
    title: "Vous payez des professionnels, vous obtenez des professionnels",
    lead: "En tant qu'investisseur ou entreprise générale, vous ignorez souvent qui un sous-traitant enverra réellement sur votre chantier. La facture affiche le même taux horaire – mais sur place, c'est tantôt un professionnel expérimenté, tantôt un débutant. Une entreprise portant le sceau SauberEko® vous garantit la vérité sur les compétences, le professionnalisme, l'expertise et l'expérience réelle du personnel qu'elle déploie.",
    gTitle: "Ce que le sceau SauberEko® vous garantit",
    g: [
      { h: "Des compétences vérifiées", t: "Chaque collaborateur certifié a passé une vérification par un expert. Ses compétences figurent au Panthéon et vous pouvez les vérifier par son nom en quelques secondes." },
      { h: "Une vraie pratique, pas seulement du papier", t: "Nous évaluons l'expérience réelle sur des projets sous la supervision d'un mentor – pas seulement des formations et des attestations rangées dans un tiroir." },
      { h: "Un prix juste selon le niveau", t: "Un sous-traitant ne peut plus facturer le même tarif pour un professionnel et pour un débutant. Le niveau de chaque collaborateur déployé est transparent." },
      { h: "L'entreprise déploie des professionnels", t: "Une entreprise titulaire du sceau s'engage à déployer des professionnels sur vos chantiers – et des débutants uniquement sous leur supervision directe." },
    ],
    cTitle: "Une différence visible sur le chantier comme sur la facture",
    without: "Sans le sceau",
    with: "Avec le sceau SauberEko®",
    rows: [
      ["Vous ne savez pas qui travaille réellement sur votre chantier", "Chaque collaborateur déployé est vérifiable par son nom"],
      ["Un prix unique pour un professionnel comme pour un débutant", "Le prix correspond au niveau réel du personnel"],
      ["La qualité n'apparaît qu'avec les erreurs et les réclamations", "L'expertise et la pratique sont vérifiées à l'avance"],
      ["Des promesses dans l'offre, sans preuve", "Une garantie indépendante sous la marque de l'UE SauberEko®"],
    ],
    note: "Comme dans notre section À propos : SauberEko® n'est pas une administration, mais une marque de professionnalisme et d'expertise. Seule une entreprise qui prouve sa qualité dans la pratique obtient le sceau.",
    ctaTitle: "Vérifiez l'entreprise avant de signer le contrat",
    ctaLead: "Au Panthéon, vous trouverez les entreprises et les professionnels certifiés. Il suffit de saisir le nom de l'entreprise ou un nom de famille.",
    cta: "Vérifier une entreprise au Panthéon",
    metaDesc: "Le sceau SauberEko® – la certitude pour l'investisseur : la vérité sur les compétences, l'expertise et l'expérience réelle du personnel déployé. Un prix juste selon le niveau réel des collaborateurs.",
  },
};

const setMeta = (name, attr, content) => {
  let el = document.head.querySelector(`meta[${attr}='${name}']`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const Hero = ({ t }) => (
  <Reveal>
    <div className="text-center">
      <div className="flex justify-center mb-7">
        <div className="relative">
          <div className="absolute inset-0 bg-[#D4AF37]/30 blur-2xl rounded-full" />
          <div className="seal-shine relative w-20 h-20 overflow-hidden flex items-center justify-center rounded-full bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black shadow-[0_10px_40px_rgba(212,175,55,0.4)] ring-1 ring-[#F2E3C6]/50">
            <Scale size={34} strokeWidth={1.75} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-3 text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-5" data-testid="investor-kicker">
        <span className="h-px w-8 bg-[#D4AF37]/60" />{t.kicker}<span className="h-px w-8 bg-[#D4AF37]/60" />
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight gold-gradient-text leading-tight" data-testid="investor-title">{t.title}</h1>
      <p className="text-white/70 text-base md:text-lg leading-relaxed mt-8 max-w-3xl mx-auto">{t.lead}</p>
      <div data-testid="investor-brand-note" className="mt-8 max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 border border-[#D4AF37]/35 bg-gradient-to-r from-[#16130a] via-[#0c0b08] to-[#16130a] px-6 py-4">
        <p className="text-[#F2E3C6] text-sm md:text-base font-display leading-relaxed">{t.brand}</p>
        <Link to="/about" data-testid="investor-about-link" className="group inline-flex items-center gap-1.5 shrink-0 text-[#D4AF37] text-sm font-semibold hover:text-[#F2E3C6] transition-colors">
          {t.aboutLink} <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  </Reveal>
);

const Guarantees = ({ t }) => (
  <Reveal>
    <h2 className="font-display text-2xl md:text-3xl text-white mt-20 mb-8 text-center">{t.gTitle}</h2>
    <div className="grid sm:grid-cols-2 gap-4">
      {t.g.map((g, i) => {
        const Ic = ICONS[i];
        return (
          <div key={i} data-testid={`investor-guarantee-${i}`} className="bg-gradient-to-b from-[#0d0d0c] to-[#080808] border border-white/10 p-6 hover:border-[#D4AF37]/40 transition-colors duration-300">
            <div className="w-11 h-11 rounded-full bg-[#D4AF37]/12 ring-1 ring-[#D4AF37]/30 flex items-center justify-center mb-4"><Ic size={20} className="text-[#D4AF37]" /></div>
            <h3 className="font-display text-lg text-white mb-2">{g.h}</h3>
            <p className="text-white/55 text-sm leading-relaxed">{g.t}</p>
          </div>
        );
      })}
    </div>
  </Reveal>
);

const Compare = ({ t }) => (
  <Reveal>
    <h2 className="font-display text-2xl md:text-3xl text-white mt-20 mb-8 text-center">{t.cTitle}</h2>
    <div className="grid md:grid-cols-2 gap-4" data-testid="investor-compare">
      <div className="border border-white/10 bg-[#0a0a0a] p-6">
        <p className="text-white/45 text-xs uppercase tracking-[0.2em] mb-5">{t.without}</p>
        <ul className="space-y-4">
          {t.rows.map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-white/55 text-sm leading-relaxed"><X size={16} className="text-red-400/70 shrink-0 mt-0.5" />{r[0]}</li>
          ))}
        </ul>
      </div>
      <div className="border border-[#D4AF37]/45 bg-gradient-to-br from-[#16130a] via-[#0c0b08] to-[#080808] p-6 shadow-[0_0_40px_rgba(212,175,55,0.08)]">
        <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-5">{t.with}</p>
        <ul className="space-y-4">
          {t.rows.map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-white/85 text-sm leading-relaxed"><Check size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />{r[1]}</li>
          ))}
        </ul>
      </div>
    </div>
    <p className="text-white/40 text-sm italic mt-8 text-center max-w-3xl mx-auto">{t.note}</p>
  </Reveal>
);

export default function PreInvestorov() {
  const { lang } = useLang();
  const t = C[lang] || C.sk;

  useEffect(() => {
    document.title = `${t.title} — SauberEko®`;
    setMeta("description", "name", t.metaDesc);
    setMeta("og:title", "property", `${t.title} — SauberEko®`);
    setMeta("og:description", "property", t.metaDesc);
  }, [t]);

  return (
    <div data-testid="investor-page" className="min-h-screen bg-[#070707] pt-28 pb-24 relative overflow-hidden">
      <div className="absolute left-1/2 top-24 -translate-x-1/2 w-[760px] h-[520px] bg-[#D4AF37]/8 blur-[150px] rounded-full pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-6">
        <Hero t={t} />
        <Guarantees t={t} />
        <Compare t={t} />
        <Reveal>
          <div className="mt-20 text-center border border-[#D4AF37]/25 bg-gradient-to-b from-[#0d0d0c] to-[#070707] p-8 sm:p-12">
            <h2 className="font-display text-2xl md:text-3xl gold-gradient-text">{t.ctaTitle}</h2>
            <p className="text-white/60 text-base mt-4 max-w-xl mx-auto leading-relaxed">{t.ctaLead}</p>
            <Link to="/hall-of-fame" data-testid="investor-hof-cta" className="group inline-flex items-center justify-center gap-2 mt-8 bg-[#D4AF37] text-black font-semibold px-8 py-4 hover:bg-[#F2E3C6] transition-colors duration-300">
              <Search size={18} /> {t.cta}
              <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform duration-300" />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
